import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { languages } from "@/i18n";
import {
  pageSEOKeys,
  organizationSchema,
  websiteSchema,
  softwareSchema,
  createBreadcrumbSchema,
} from "@/lib/seoData";

const SITE_URL = "https://www.seaventis.com";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;
const SITE_NAME = "Seaventis";
const LANG_CODES = languages.map((l) => l.code);


/**
 * Automatically generates breadcrumb items from a pathname.
 */
function autoBreadcrumbs(pathname: string) {
  if (pathname === "/") return [{ name: "Home", url: "/" }];
  const segments = pathname.split("/").filter(Boolean);
  const items = [{ name: "Home", url: "/" }];
  let currentPath = "";
  for (const seg of segments) {
    currentPath += `/${seg}`;
    const label = seg
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
    items.push({ name: label, url: currentPath });
  }
  return items;
}

/**
 * Resolves SEO data for a given path, using i18n keys when available.
 */
function useResolveSEO(pathname: string): { title: string; description: string; image?: string; keywords?: string } {
  const { t } = useTranslation();

  // Try i18n key-based lookup first
  const keys = pageSEOKeys[pathname];
  if (keys) {
    return {
      title: t(keys.titleKey),
      description: t(keys.descriptionKey),
      image: keys.image,
      keywords: keys.keywords,
    };
  }

  // Parent match for dynamic routes
  const parentPath = "/" + pathname.split("/").filter(Boolean)[0];
  const parentKeys = pageSEOKeys[parentPath];
  if (parentKeys) {
    const slug = pathname.split("/").filter(Boolean).slice(1).join(" ");
    const prettySlug = slug
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
    return {
      title: `${prettySlug} — ${t(parentKeys.titleKey)}`,
      description: t(parentKeys.descriptionKey),
      image: parentKeys.image,
      keywords: parentKeys.keywords,
    };
  }


  return {
    title: "Seaventis",
    description: t("seo.home.description"),
  };
}

/**
 * AutoSEO — drop-in component that automatically applies comprehensive SEO
 * based on the current route and language.
 */
export function AutoSEO() {
  const { pathname: rawPathname } = useLocation();
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";

  // Strip language prefix so SEO keys like "/features/fleet" match
  const langPrefixRegex = new RegExp(`^/${lang}(?=/|$)`);
  const pathname = rawPathname.replace(langPrefixRegex, "") || "/";

  const isNonProductionDomain =
    typeof window !== "undefined" &&
    !window.location.hostname.endsWith("seaventis.com");

  const seo = useResolveSEO(pathname);

  // Build title: ensure keyword portion isn't truncated by suffix
  const suffix = pathname === "/" ? ` | ${SITE_NAME}` : ` | ${SITE_NAME}`;
  const maxTitleLen = 60;
  const availableForTitle = maxTitleLen - suffix.length;
  const truncatedTitle = seo.title.length > availableForTitle
    ? seo.title.slice(0, availableForTitle - 1).trimEnd() + "…"
    : seo.title;
  const fullTitle = `${truncatedTitle}${suffix}`;
  const safeTitle = fullTitle;
  const safeDesc = seo.description.length > 160
    ? seo.description.slice(0, 157).trimEnd() + "…"
    : seo.description;
  const ogImage = seo.image || DEFAULT_IMAGE;
  const canonicalUrl = `${SITE_URL}/${lang}${pathname === "/" ? "" : pathname}`;

  const breadcrumbs = autoBreadcrumbs(pathname);
  const breadcrumbSchema = createBreadcrumbSchema(breadcrumbs);

  const isHome = pathname === "/";
  const isNewsArticle = /^\/yacht-industry-news\/[^/]+/.test(pathname);
  const articleOwnsImageAlt = isNewsArticle && pathname !== "/yacht-industry-news/archive";
  const ogType = isNewsArticle ? "article" : "website";

  const schemas = isHome
    ? [organizationSchema, websiteSchema, softwareSchema, breadcrumbSchema]
    : [breadcrumbSchema];

  const pathWithoutLang = pathname;

  const OG_LOCALE_MAP: Record<string, string> = {
    en: "en_US", fr: "fr_FR", es: "es_ES", it: "it_IT",
    de: "de_DE", zh: "zh_CN", sr: "sr_RS", el: "el_GR", ru: "ru_RU",
  };
  const currentLocale = OG_LOCALE_MAP[lang] || `${lang}_${lang.toUpperCase()}`;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{safeTitle}</title>
      <meta name="description" content={safeDesc} />
      {seo.keywords && <meta name="keywords" content={seo.keywords} />}
      <meta name="robots" content={isNonProductionDomain ? "noindex,nofollow" : "index,follow"} />
      <meta name="googlebot" content={isNonProductionDomain ? "noindex,nofollow" : "index,follow"} />
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang alternate links */}
      {LANG_CODES.map((code) => (
        <link
          key={code}
          rel="alternate"
          hrefLang={code}
          href={`${SITE_URL}/${code}${pathWithoutLang === "/" ? "" : pathWithoutLang}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/en${pathWithoutLang === "/" ? "" : pathWithoutLang}`} />

      <meta property="og:title" content={safeTitle} />
      <meta property="og:description" content={safeDesc} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      {!articleOwnsImageAlt && <meta property="og:image:alt" content={`${seo.title} — Seaventis Intelligent Yacht Software`} />}
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={currentLocale} />
      {/* og:locale:alternate for other languages */}
      {LANG_CODES.filter((code) => code !== lang).map((code) => (
        <meta key={`og-alt-${code}`} property="og:locale:alternate" content={OG_LOCALE_MAP[code] || `${code}_${code.toUpperCase()}`} />
      ))}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@seaventis" />
      <meta name="twitter:title" content={safeTitle} />
      <meta name="twitter:description" content={safeDesc} />
      <meta name="twitter:image" content={ogImage} />
      {!articleOwnsImageAlt && <meta name="twitter:image:alt" content={`${seo.title} — Seaventis Intelligent Yacht Software`} />}

      {/* Individual JSON-LD scripts for each schema */}
      {schemas.map((schema, i) => (
        <script key={`ld-${i}`} type="application/ld+json">{JSON.stringify(schema)}</script>
      ))}
    </Helmet>
  );
}
